import { RITUAL, SIDE, TIMING } from '../../constants';
import { endMinutes, fromMinutes, toMinutes } from './format';

// Reads over the event entries. The entries themselves are the server's, so
// nothing here writes: a caller that wants a change sends a PATCH and takes
// the entries back out of the response.

// A day holds both sides of the couple: an event carries its own side, and the
// card says which, so the list is never split by one.
export const datesOf = (events) => {
  const seen = {};
  const out = [];
  (events || []).forEach((x) => {
    if (x.date && !seen[x.date]) {
      seen[x.date] = true;
      out.push(x.date);
    }
  });
  return out.sort();
};

// Getting ready is shot right before the ceremony, whoever it belongs to.
// Matched on the name because it arrives both as a package event and as a
// ritual the couple added, and neither says what it is. The same two words
// the server holds the stored order to.
const PINNED_EVENT = 'getting ready';

const nameKey = (entry) =>
  String((entry || {}).name || '').trim().replace(/\s+/g, ' ').toLowerCase();

export const isPinnedEvent = (entry) => nameKey(entry).indexOf(PINNED_EVENT) >= 0;

// The ceremony itself, by the category flag ops sets and the server sends.
// Not read off the name: a day can carry a pre wedding and a post wedding,
// and neither is the thing being got ready for.
const isPinAnchor = (entry) => !!(entry || {}).is_actual_wedding;

/**
 * One day, with every getting ready moved in front of the ceremony.
 *
 * Both sides can have one, so they move as a block and keep the order they
 * already had between them. A day with no ceremony on it has nothing to
 * hold them in front of, so they are left where they are.
 */
export const pinOrder = (day) => {
  const list = day || [];
  const anchor = list.find(isPinAnchor);
  if (!anchor) return list;
  const moving = list.filter((x) => isPinnedEvent(x) && x !== anchor);
  if (!moving.length) return list;
  const out = [];
  list.forEach((x) => {
    if (moving.indexOf(x) >= 0) return;
    if (x === anchor) out.push(...moving);
    out.push(x);
  });
  return out;
};

export const eventsOnDate = (events, date) =>
  pinOrder((events || []).filter((x) => x.date === date));

export const resolveDate = (events, wanted) => {
  const list = datesOf(events);
  if (wanted && list.indexOf(wanted) >= 0) return wanted;
  return list[0] || null;
};

export const entryById = (events, id) =>
  (events || []).find((x) => String(x.id) === String(id)) || null;

/**
 * Whether an entry's timing sits outside the package window for its date. The
 * server decides the same thing and owns the outcome; this only drives what
 * the card says before the answer comes back.
 */
export const isOutsideWindow = (entry, windows) => {
  const w = (windows || {})[entry.date];
  if (!w || !entry.start || !entry.end) return false;
  const winStart = toMinutes(w.start);
  const winEnd = endMinutes(w.start, w.end);
  let start = toMinutes(entry.start);
  let end = endMinutes(entry.start, entry.end);
  // A window running past midnight carries the small hours with it, so a
  // timing that starts before it opens belongs to that tail rather than to
  // the morning before coverage began.
  if (winEnd > 1440 && start < winStart) {
    start += 1440;
    end += 1440;
  }
  return start < winStart || end > winEnd;
};

// An entry with something awaiting review: either the server's own status, or
// values in `pending` that ops has not approved. Never presented as saved.
export const isPending = (entry) =>
  entry.timing_status === TIMING.PENDING || Object.keys(entry.pending || {}).length > 0;

export const isTimingSet = (entry) => !!(entry.start && entry.end);

/**
 * Whether the couple has answered this event. Nothing on the form is
 * compulsory, so closing the card is the answer and the only thing that
 * closes it. Times alone never settle a card: typing the second one would
 * shut it with the venue and the map link still empty.
 */
export const isAnswered = (entry) => !!entry.confirmed;

/**
 * The timeline's stops, in the order the couple walks them. The interview
 * comes first: whether they want one, and when each group sits, is asked
 * before they order and time the day. The question is only asked where this
 * form is the one asking it, and the sittings only follow a yes.
 */
export const timelineStopKeys = ({ wantsInterview, askInterview }) =>
  (askInterview ? ['interview_ask'] : [])
    .concat(wantsInterview === true ? ['interviews'] : [])
    .concat(['order', 'timings']);

export const customRituals = (events) => (events || []).filter((x) => x.kind === RITUAL);

/**
 * The ranges already spoken for on this entry's day and side. Different side
 * events are not taken: concurrent bride and groom coverage is normal, and the
 * server allows it.
 */
/**
 * How long a couple shot holds the day, and on which end of its event.
 *
 * The shot is not an event of its own, so it never appears in the list. It
 * still occupies the crew, so the minutes beside its event are not free.
 */
// The half hour the couple's own interview holds in front of an event. Only
// the one ceremony that carries it ever has this, and the server sends the
// same number it reserves, so the card and the server agree on the day.
export const interviewLead = (entry) =>
  Number((entry || {}).interview_minutes || 0) || 0;

/**
 * The half hour the day holds for getting ready, read off the event.
 *
 * Only the ceremony the day is built around carries it. Both sides are
 * shot at the same time by their own crews, so it is one half hour for the
 * two of them rather than one each.
 */
export const readyLead = (entry) =>
  Number((entry || {}).ready_minutes || 0) || 0;

export const shotPad = (entry) => {
  // Getting ready first, then the sitting, then the shot: each runs in
  // front of the one after it, and the event is last.
  const lead = readyLead(entry) + interviewLead(entry);
  const shot = entry.couple_shot;
  const minutes = shot && shot.minutes ? Number(shot.minutes) : 0;
  if (!minutes) return { before: lead, after: 0 };
  // A shot taken during its event is inside it, so it holds nothing either
  // side, and the interview sits straight in front of the event. The other
  // two hang off an end: a shot running into the event puts the interview
  // in front of the shot rather than in front of the event.
  if (shot.position === 'during') return { before: lead, after: 0 };
  return shot.position === 'end'
    ? { before: lead, after: minutes }
    : { before: lead + minutes, after: 0 };
};

/**
 * When the couple's own interview runs, read off the event it hangs on.
 *
 * Half an hour ending where the couple is first needed. The block exists
 * from the moment the ceremony does, the way the couple shot does: before
 * the ceremony has a time there is nothing to put on it, so it says how
 * long it runs and waits for the rest.
 */
export const interviewBlock = (entry) => {
  const lead = interviewLead(entry);
  if (!lead) return null;
  const start = toMinutes(entry && entry.start);
  if (start === null || Number.isNaN(start)) {
    return { minutes: lead, start: '', end: '' };
  }
  // Getting ready runs in front of the sitting, so its half hour is given
  // back: the sitting ends where the couple is first needed.
  const from = start - shotPad(entry).before + readyLead(entry);
  return { minutes: lead, start: fromMinutes(from), end: fromMinutes(from + lead) };
};

/**
 * When getting ready runs, read off the event it hangs on.
 *
 * The first thing on the day: half an hour in front of the sitting and the
 * shot both. Like the sitting, it exists from the moment the ceremony
 * does, so before the ceremony has a time it says how long it runs and
 * waits for the rest.
 */
export const readyBlock = (entry) => {
  const minutes = readyLead(entry);
  if (!minutes) return null;
  const start = toMinutes(entry && entry.start);
  if (start === null || Number.isNaN(start)) {
    return { minutes, start: '', end: '' };
  }
  const from = start - shotPad(entry).before;
  return { minutes, start: fromMinutes(from), end: fromMinutes(from + minutes) };
};

// A minute sits in a taken range when it falls inside it, on this day or on
// the same range rolled past midnight.
export const inRange = (minute, ranges) =>
  ranges.some(
    (r) =>
      (minute >= r.start && minute < r.end) ||
      (minute + 1440 >= r.start && minute + 1440 < r.end)
  );

// A span from `start` to `end` may not cross a range someone else holds.
export const crosses = (start, end, ranges) =>
  ranges.some((r) => start < r.end && end > r.start);

/**
 * Whether two events are on a side that cannot run at once.
 *
 * The same side, plainly. And an event billed to both sides, because the
 * couple is in it: it cannot run beside either of their own events.
 */
export const sidesClash = (a, b) => a === b || a === SIDE.BOTH || b === SIDE.BOTH;

export const takenRanges = (events, entry) =>
  eventsOnDate(events, entry.date)
    .filter(
      (other) =>
        String(other.id) !== String(entry.id) &&
        sidesClash(other.side, entry.side) &&
        isTimingSet(other)
    )
    .map((other) => {
      const pad = shotPad(other);
      return {
        id: other.id,
        name: other.name,
        side: other.side,
        // The event's own hours, plus the couple shot that rides on it.
        start: toMinutes(other.start) - pad.before,
        end: endMinutes(other.start, other.end) + pad.after,
        shot: pad.before || pad.after,
      };
    });

/**
 * The minutes this side's interview sittings already hold on one day.
 *
 * A sitting is the same crew on the same side, so an event cannot run over
 * it any more than it can run over another event. An event billed to both
 * sides is blocked by either side's sitting.
 */
export const interviewHolds = (interviews, date, side) =>
  (interviews || [])
    .filter((row) => row.date === date && row.start && row.end
                     && sidesClash(row.side, side))
    .map((row) => ({
      id: `interview:${row.side}:${row.group}`,
      name: `${row.label || row.group} interview`,
      side: row.side,
      start: toMinutes(row.start),
      end: endMinutes(row.start, row.end),
      shot: 0,
      interview: true,
    }));

/**
 * Same side overlaps are the server's refusal. Different side overlaps are
 * normal, because concurrent bride and groom coverage is how a wedding runs,
 * so they are never warned about.
 */
export const sameSideOverlap = (events, entry) => {
  if (!isTimingSet(entry)) return null;
  const start = toMinutes(entry.start);
  const end = endMinutes(entry.start, entry.end);
  return (
    eventsOnDate(events, entry.date).find((other) => {
      if (String(other.id) === String(entry.id)) return false;
      if (!sidesClash(other.side, entry.side)) return false;
      if (!isTimingSet(other)) return false;
      return toMinutes(other.start) < end && endMinutes(other.start, other.end) > start;
    }) || null
  );
};

/**
 * Suggested duration for an entry, in hours, as the payload states it. The
 * server decides it off the event's category, so the guide follows what ops
 * has flagged rather than a guess at the event's name.
 */
export const suggestedHours = (entry) => {
  if (!entry || !entry.suggested_hours) return null;
  return Number(entry.suggested_hours);
};
